/**
 * Процессор телеметрии боя
 * Вынесено из legacy-app.js для модульности
 * 
 * Обрабатывает телеметрию боя: форматирует события, группирует по раундам,
 * строит timeline статус-эффектов.
 */

import {
  formatTelemetryEventName,
  formatTelemetryEvent,
  processTelemetryByRound,
} from "./telemetryFormatter.js";
import {
  processBattleStatusEffects,
  processEventStatusEffects,
  cleanupExpiredStatusEffects,
  serializeStatusEffects,
} from "./statusEffects.js";

/**
 * @typedef {Object} TelemetryProcessor
 * @property {Function} formatEventName - Форматирует имя события телеметрии
 * @property {Function} formatEvent - Форматирует событие телеметрии
 * @property {Function} processByRound - Обрабатывает телеметрию по раундам
 * @property {Function} buildStatusTimeline - Строит timeline статус-эффектов
 */

/**
 * Создает процессор телеметрии боя
 * 
 * @param {Object} params - Параметры процессора
 * @param {Function} params.getCombatantName - Функция получения имени бойца (te)
 * @param {Function} params.formatPercent - Функция форматирования процентов (K)
 * @param {Function} params.processBattleStatusEffects - Функция обработки статус-эффектов боя (is)
 * @param {Function} params.processEventStatusEffects - Функция обработки статус-эффектов события (as)
 * @param {Function} params.cleanupExpiredStatusEffects - Функция очистки истекших статус-эффектов (ss)
 * @param {Function} params.serializeStatusEffects - Функция сериализации статус-эффектов (os)
 * @param {Function} params.formatStatusName - Функция форматирования имени статуса (Yt)
 * @returns {TelemetryProcessor} Объект с методами процессора
 */
export function createTelemetryProcessor({
  getCombatantName,
  formatPercent,
  processBattleStatusEffects: processBattleStatuses,
  processEventStatusEffects: processEventStatuses,
  cleanupExpiredStatusEffects: cleanupStatuses,
  serializeStatusEffects: serializeStatuses,
}) {
  /**
   * Форматирует имя события телеметрии
   * 
   * @param {string} eventName - Имя события (например, "effect:poison")
   * @returns {string} Отформатированное имя
   */
  function formatEventName(eventName) {
    return formatTelemetryEventName(eventName, formatStatusName);
  }

  /**
   * Форматирует событие телеметрии
   * 
   * @param {Object} event - Событие телеметрии
   * @param {string} event.event - Тип события (должен начинаться с "effect:")
   * @param {Array} panels - Панели бойцов для получения имен
   * @returns {string|null} Отформатированное событие или null, если событие невалидно
   */
  function formatEvent(event, panels) {
    if (!event || typeof event !== "object") return null;
    
    const eventName = event.event;
    if (!eventName || !eventName.startsWith("effect:")) return null;
    
    return formatTelemetryEvent(
      event,
      (id) => getCombatantName(panels, id),
      formatPercent,
      formatEventName
    );
  }

  /**
   * Обрабатывает телеметрию по раундам
   * 
   * Группирует события телеметрии по раундам и форматирует их.
   * 
   * @param {Object} battleData - Данные боя
   * @param {Array} battleData.telemetry - Массив событий телеметрии
   * @param {Array} panels - Панели бойцов для получения имен
   * @returns {Object} Обработанная телеметрия по раундам (ключ - номер раунда)
   */
  function processByRound(battleData, panels) {
    if (!battleData || !Array.isArray(battleData.telemetry)) return {};
    
    const panelsMap = new Map((panels || []).map((panel) => [String(panel.id), panel]));
    const getCombatantNameFromPanels = (id) => {
      const panel = panelsMap.get(String(id));
      return panel ? (panel.owner ? `${panel.owner} (${panel.name})` : panel.name) : null;
    };
    
    return processTelemetryByRound(battleData, panels, (event) => {
      return formatTelemetryEvent(
        event,
        getCombatantNameFromPanels,
        formatPercent,
        formatEventName
      );
    });
  }

  /**
   * Строит timeline статус-эффектов
   * 
   * Обрабатывает телеметрию и строит timeline статус-эффектов для каждого раунда.
   * 
   * @param {Object} battleData - Данные боя
   * @param {Array} battleData.telemetry - Массив событий телеметрии
   * @param {Array} battleData.combatants - Массив бойцов
   * @param {Array} timeline - Timeline боя (массив записей с entry.round)
   * @returns {Array<Object>} Timeline статус-эффектов, каждый элемент содержит round и statuses
   */
  function buildStatusTimeline(battleData, timeline) {
    if (!battleData || !Array.isArray(timeline)) return [];
    
    const telemetry = Array.isArray(battleData.telemetry)
      ? battleData.telemetry.slice().sort((a, b) => (a.round ?? 0) - (b.round ?? 0))
      : [];
    
    const combatantsMap = new Map(
      (battleData.combatants || []).map((combatant) => [
        String(
          combatant.id ?? combatant.panelId ?? combatant.slotId ?? combatant.sourceId ?? combatant.combatantId ?? ""
        ),
        combatant,
      ])
    );
    
    const statusEffects = new Map();
    let currentRound = 0;
    let telemetryIndex = 0;
    
    // Инициализируем статус-эффекты из боя
    processBattleStatuses(statusEffects, battleData, currentRound);
    
    return timeline.map((entry) => {
      const round = entry?.entry?.round;
      
      if (typeof round === "number") {
        currentRound = round;
      }
      
      // Очищаем истекшие статус-эффекты
      cleanupStatuses(statusEffects, currentRound);
      
      // Обрабатываем телеметрию до текущего раунда
      while (
        telemetryIndex < telemetry.length &&
        (telemetry[telemetryIndex].round ?? 0) <= currentRound
      ) {
        processEventStatuses(telemetry[telemetryIndex], statusEffects, combatantsMap, currentRound);
        telemetryIndex += 1;
      }
      
      return { round: currentRound, statuses: serializeStatuses(statusEffects) };
    });
  }

  return {
    formatEventName,
    formatEvent,
    processByRound,
    buildStatusTimeline,
  };
}

